const WEIGHED_PRODUCT_BARCODE_PATTERN = /^\d{5}$/;
const RESERVED_EMPTY_SLOT_PREFIX = "99";

export const normalizeStoreBarcodes = (barcodes: unknown): string[] => {
  if (!Array.isArray(barcodes)) return [];

  const values = new Set<string>();

  barcodes.forEach((barcode) => {
    const value = String(barcode).trim();
    if (value) values.add(value);
  });

  return Array.from(values);
};

export const haveSameStoreBarcodes = (left: unknown, right: unknown): boolean => {
  const leftValues = new Set(normalizeStoreBarcodes(left));
  const rightValues = new Set(normalizeStoreBarcodes(right));

  if (leftValues.size !== rightValues.size) return false;

  return Array.from(leftValues).every((barcode) => rightValues.has(barcode));
};

/**
 * A store's barcodes travel in a product PUT only when they changed, or when
 * the sold-by-unit flag changed — the server then re-checks every store's
 * barcodes against the new flag, so all of them are sent to get per-store
 * errors back. Barcodes that stay behind are never re-validated server-side,
 * which is why only the ones that travel are validated here too.
 */
export const shouldSendStoreBarcodes = (
  savedBarcodes: unknown,
  barcodes: unknown,
  savedSoldByUnit: unknown,
  soldByUnit: unknown,
): boolean =>
  Boolean(savedSoldByUnit) !== Boolean(soldByUnit) ||
  !haveSameStoreBarcodes(savedBarcodes, barcodes);

export type StoreBarcodeValidationError =
  | "format"
  | "reserved_prefix"
  | "reserved_for_weighed"
  | null;

/**
 * Product barcode rules that can be decided without knowing another product's
 * data. Cross-product uniqueness remains a server-side submit validation.
 * The five-digit shape belongs to scale labels, so a weighed product must use
 * it and any other product must not.
 */
export const validateStoreBarcodes = (
  barcodes: unknown,
  isWeighedProduct: boolean,
): StoreBarcodeValidationError => {
  const values = normalizeStoreBarcodes(barcodes);

  if (!isWeighedProduct) {
    return values.some((barcode) => WEIGHED_PRODUCT_BARCODE_PATTERN.test(barcode))
      ? "reserved_for_weighed"
      : null;
  }

  if (values.some((barcode) => !WEIGHED_PRODUCT_BARCODE_PATTERN.test(barcode))) {
    return "format";
  }

  if (values.some((barcode) => barcode.startsWith(RESERVED_EMPTY_SLOT_PREFIX))) {
    return "reserved_prefix";
  }

  return null;
};
